/* eslint-disable */

import LazyLoad from "vanilla-lazyload";
import inView from "in-view";
import gsap from "gsap";
import Flickity from "flickity-fade";
import { CustomEase } from "gsap/CustomEase";
import Cookies from "js-cookie";
import lottie from "lottie-web";
import ParallaxElement from "./utils/parallax";

const HTML = document.querySelector("html");

gsap.registerPlugin(CustomEase);

class App {
  constructor() {
    this.lazyload = new LazyLoad({
      unobserve_entered: true,
      elements_selector: ".js-lazy",
      threshold: window.innerHeight * 0.8,
      // eslint-disable-next-line camelcase
      callback_enter: ($el) => {
        if ($el.classList.contains("js-lazy-error-placeholder")) {
          $el.lazyLoadPlaceholder = $el.src;
        } else if ($el.dataset.src == undefined) {
          const figure = $el.closest("figure");
          if (figure) figure.classList.add("img-is-loaded");
        }
      },
      callback_loaded: (el) => {
        const figure = el.closest("figure");
        if (figure) figure.classList.add("img-is-loaded");
      },
      // eslint-disable-next-line camelcase
      callback_error: ($el) => {
        if ($el.lazyLoadPlaceholder) {
          $el.src = $el.lazyLoadPlaceholder;
        }
      },
    });

    /** * HEADER */
    const header = document.querySelector(".main-header");
    if (header) {
      window.addEventListener("scroll", () => {
        const darkSections = document.querySelectorAll("section.bg-gray-200");

        if (darkSections) {
          for (const section of darkSections) {
            const bound = section.getBoundingClientRect();
            if (
              bound.y <= header.offsetHeight / 2 &&
              bound.bottom > header.offsetHeight / 2
            ) {
              HTML.classList.add("header-black");
              break;
            } else {
              HTML.classList.remove("header-black");
            }
          }
        }
      });

      window.dispatchEvent(new Event("scroll"));
    }
    this.cursor();
    this.anchor();

    /** Loader */
    const loader = document.querySelector(".loader");
    if (loader && Cookies.get("already-load") == undefined) {
      const tl = gsap.timeline({
        paused: true,
        delay: 0.1,
        defaults: {
          duration: 2,
          ease: CustomEase.create(
            "custom",
            "M0,0 C0,0 0.104,0.19 0.149,0.204 0.188,0.218 0.266,0.222 0.303,0.24 0.339,0.258 0.456,0.559 0.49,0.583 0.523,0.607 0.57,0.627 0.6,0.656 0.632,0.688 0.893,0.734 0.915,0.783 0.938,0.836 1,1 1,1 "
          ),
        },
        onComplete: () => {
          const stateCheck = setInterval(() => {
            if (document.readyState === "complete") {
              clearInterval(stateCheck);

              Cookies.set("already-load", true, { expires: 1 });
              gsap.to(loader, { autoAlpha: 0 });
              this.init();
            }
          }, 100);
        },
      });

      const bar = loader.querySelector(".bar");
      const percent = loader.querySelector(".percent");

      tl.fromTo(
        loader.querySelectorAll(".el"),
        {
          opacity: 0,
          y: "3rem",
        },
        {
          opacity: 1,
          y: "0%",
          ease: "expo.out",
          duration: 0.6,
          stagger: 0.1,
        }
      )
        .to(bar, { width: "100%" }, "start")
        .from(
          percent,
          {
            textContent: `${0}%`,
            snap: { textContent: 1 },
          },
          "start"
        );

      tl.play();
    } else {
      gsap.set(loader, { autoAlpha: 0 });
      this.init();
    }
  }

  init() {
    HTML.classList.remove("no-scroll");
    this.parallax();
    this.marquee();
    this.progressPage();
    this.timeline();
    this.sliderGallery();
    this.slider();
    this.videoPlayer();
    this.ajax();
    this.bodymovin();
    this.inViewport();
  }

  cursor() {
    const cursor = document.querySelector(".cursor");

    if (cursor) {
      document.body.addEventListener("mouseenter", () => {
        gsap.to(cursor, { duration: 0.3, scale: 1 });
      });
      document.body.addEventListener("mouseleave", () => {
        gsap.to(cursor, { duration: 0.3, scale: 0 });
      });

      window.addEventListener("mousemove", (e) => {
        gsap.to(cursor, { duration: 0.3, scale: 1 });
        gsap.set(cursor, { left: e.clientX, top: e.clientY });
      });
    }
  }

  anchor() {
    /** ANCHOR */
    const anchors = document.querySelectorAll(
      'a[href^="#"]:not([href="#"]), a[href^="/#"]:not([href="#"])'
    );

    const header = document.querySelector(".main-header");
    if (anchors) {
      anchors.forEach((anchor) => {
        anchor.addEventListener("click", (e) => {
          const target = document.querySelector(
            anchor.getAttribute("href").replace("/#", "#")
          );

          if (target) {
            e.preventDefault();
            let offset = 0;
            const bound = target.getBoundingClientRect();

            if (header) {
              offset = header.offsetHeight;
            }

            gsap.to(document.querySelectorAll("html, body"), {
              duration: 0.8,
              scrollTop: window.scrollY + bound.y - offset,
              ease: "expo.out",
            });
          }
        });
      });
    }
  }

  parallax() {
    // Parallax
    if (window.innerWidth > 1022) {
      document.querySelectorAll(".js-parallax").forEach((el) => {
        const _p = new ParallaxElement(el, ".js-parallax", false);
        _p.teardown();
      });
    }
  }

  async ajax() {
    const triggers = document.querySelectorAll("[data-ajax]");

    if (triggers) {
      triggers.forEach(async (trigger) => {
        trigger.addEventListener("click", async (e) => {
          e.preventDefault();

          const title = document.querySelector("title");
          HTML.classList.add("ajax-loading");

          try {
            const url = new URL(trigger.dataset.ajax);

            const res = await fetch(trigger.dataset.ajax);
            const txt = await res.text();
            const html = document.createElement("html");
            html.innerHTML = txt;
            const main = html.querySelector(".main");

            const popin = document.createElement("div");
            popin.classList.add("popin");
            popin.append(main);

            function closePopin() {
              HTML.classList.remove(
                "show-popin",
                "header-black",
                "hide-header"
              );
              history.pushState({}, title.innerText, url.origin);

              setTimeout(() => {
                popin.remove();
              }, 400);
            }

            const close = main.querySelector(".trigger-close");
            if (close) {
              close.addEventListener("click", (e) => {
                e.preventDefault();
                closePopin();
              });
            }

            document.body.append(popin);

            history.pushState(
              {},
              popin.querySelector("h1").innerText,
              url.pathname
            );
            this.lazyload.update();
            this.inViewport();
            this.sliderGallery();

            setTimeout(() => {
              HTML.classList.add("show-popin", "header-black", "hide-header");
              HTML.classList.remove("ajax-loading");
              window.dispatchEvent(new Event("resize"));
            }, 300);

            function postateChange(e) {
              closePopin();
              window.removeEventListener("popstate", postateChange);
            }
            window.addEventListener("popstate", postateChange);
          } catch {
            HTML.classList.remove("ajax-loading");
          }
        });
      });
    }
  }

  progressPage() {
    const progress = document.querySelector('.progress-page');

    if( progress ) {
      window.addEventListener('scroll', () => {
        const scrollY = window.scrollY;
        const wH = window.innerHeight;
        const h = document.body.offsetHeight;

        gsap.set(progress, { width : `${(scrollY)/ (h- wH) * 100}%`} )
      })

      window.dispatchEvent(new Event('scroll'))
    }
  }

  bodymovin() {
    const tags = document.querySelectorAll("[data-bodymovin]");

    if (tags) {
      tags.forEach((tag) => {
        tag.addEventListener("inview", () => {
          if (!tag.classList.contains("loaded")) {
            const anim = tag.dataset.bodymovin;
            // const path = `${WPTEMPLATE}/static/bodymovin/${anim}/data.json`;
            fetch(anim).then((response) => {
              if (response.status == 200) {
                const l = lottie.loadAnimation({
                  container: tag,
                  renderer: "svg",
                  loop: true,
                  autoplay: true,
                  path: anim
                });
                tag.classList.add("loaded");
                tag.lottie = l;
              }
            });
          } else if (tag.lottie) {
            tag.lottie.play();
          }
        });

        tag.addEventListener("outview", () => {
          if (tag.lottie) {
            tag.lottie.pause();
          }
        });
      });
    }
  }

  timeline() {
    const elts = document.querySelectorAll(".timeline");

    if (elts) {
      elts.forEach((el) => {
        const counter = el.querySelector(".counter .val");

        new Flickity(el, {
          cellAlign: "left",
          cellSelector: ".item",
          pageDots: false,
          selectedAttraction: 0.01,
          friction: 0.17,

          on: {
            select: (index) => {
              if (counter) {
                counter.innerHTML = `${index < 9 ? "0" : ""}${index + 1}`;
              }
            },
          },
        });
      });
    }
  }

  sliderGallery() {
    const sliders = document.querySelectorAll(".slider-gallery");

    sliders.forEach((slider) => {
      const flkty = new Flickity(slider, {
        wrapAround: true,
        prevNextButtons: false,
        adaptiveHeight: true,
        fade: true,
      });
    });
  }

  slider() {
    const sliders = document.querySelectorAll(".slider");

    sliders.forEach((slider) => {
      const flkty = new Flickity(slider, {
        wrapAround: true,
        pageDots: false,
        prevNextButtons: true,
        cellSelector: ".item",
        adaptiveHeight: true,
        autoPlay: 5000,
        selectedAttraction: 0.01,
        friction: 0.25,
      });

      flkty.pausePlayer();

      slider.addEventListener("inview", () => {
        flkty.playPlayer();
      });
      slider.addEventListener("outview", () => {
        flkty.pausePlayer();
      });
    });
  }

  marquee() {
    const marquees = document.querySelectorAll(".marquee .line");

    let pas = 0.75;

    function wheel(e) {
      const orgin = 0.75;
      pas = (e.deltaY < 0 ? -1 : 1) * orgin;
    }

    if (marquees) {
      window.addEventListener("wheel", wheel);

      window.addEventListener("unmount", () => {
        window.removeEventListener("wheel", wheel);
      });

      marquees.forEach((el) => {
        const flkty = new Flickity(el, {
          cellAlign: "left",
          freeScroll: true,
          prevNextButtons: false,
          pageDots: false,
          wrapAround: true,
          draggable: false,
        });

        flkty.request = 0;

        function play() {
          flkty.x -= Number.parseFloat(el.dataset.dir) * pas;
          flkty.settle(flkty.x);
          flkty.request = window.requestAnimationFrame(play);
        }

        function launch() {
          el.removeEventListener("inview", launch);
          play();
        }

        el.addEventListener("inview", launch);
        el.addEventListener("outview", () => {
          window.cancelAnimationFrame(flkty.request);
          el.addEventListener("inview", launch);
        });

        window.addEventListener("unmount", () => {
          flkty.destroy();
          window.cancelAnimationFrame(flkty.request);
          el.removeEventListener("inview", launch);
        });
      });
    }
  }

  inViewport() {
    // In viewport
    inView.offset({
      bottom: window.innerHeight * 0.1,
    });
    inView(".js-reveal:not(.is-visible)")
      .on("enter", function (elt) {
        elt.dispatchEvent(new Event("inview"));
        elt.classList.add("is-visible");
        elt.classList.remove("not-visible");

        const video = elt.querySelector("video");

        if (
          (elt.tagName == "VIDEO" && elt.dataset.autoplay == "true") ||
          (video && video.dataset.autoplay == "true")
        ) {
          const v = video || elt;
          const playPromise = v.play();
          if (playPromise !== null) {
            playPromise.catch(() => {
              v.play();
            });
          }
        }
      })
      .on("exit", function (elt) {
        const video = elt.querySelector("video");
        if (
          (elt.tagName == "VIDEO" && elt.dataset.autoplay != "") ||
          (video && video.dataset.autoplay != "")
        ) {
          const v = video || elt;
          v.pause();
        }

        elt.dispatchEvent(new Event("outview"));
        elt.classList.add("not-visible");
      });
  }

  videoPlayer() {
    const players = document.querySelectorAll(".player-js");

    if (players) {
      players.forEach((player) => {
        const video = player.querySelector("video:not(.preview)");

        if (video) {
          /** BUILD CONTROLS */
          const controls = document.createElement("div");
          controls.classList.add("controls");
          player.append(controls);

          const playPause = document.createElement("a");
          playPause.classList.add("play-pause");
          controls.append(playPause);

          const progress = document.createElement("div");
          progress.classList.add("progress");
          controls.append(progress);
          const bar = document.createElement("div");
          bar.classList.add("bar");
          progress.append(bar);

          const sound = document.createElement("a");
          sound.classList.add("sound");
          controls.append(sound);

          const fullscreen = document.createElement("a");
          fullscreen.classList.add("fullscreen");
          controls.append(fullscreen);

          const btnPlay = player.querySelector("[data-play]");
          if (btnPlay) {
            btnPlay.addEventListener("click", (e) => {
              e.preventDefault();
              video.play();
            });
          }
          const trigger = player.querySelector("[data-trigger-player]");
          if (trigger) {
            trigger.addEventListener("click", (e) => {
              e.preventDefault();
              video.play();
            });
          }

          /** EVENTS LISTENER */
          // CLICK
          playPause.addEventListener("click", (e) => {
            e.preventDefault();
            if (video.paused) video.play();
            else video.pause();
          });

          // PROGRESS CLICK
          progress.addEventListener("click", (e) => {
            const bounding = progress.getBoundingClientRect();
            const percent = (e.clientX - bounding.x) / bounding.width;

            video.currentTime = percent * video.duration;
          });

          // CLICK
          video.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (video.paused) video.play();
            else video.pause();
          });

          // FULLSCREEN
          fullscreen.addEventListener("click", (e) => {
            e.preventDefault();
            if (!video.fullscreenElement) {
              video.requestFullscreen();
            } else if (video.exitFullscreen) {
              video.exitFullscreen();
            }
          });

          // SOUND
          sound.addEventListener("click", (e) => {
            e.preventDefault();
            if (!video.muted) {
              video.muted = true;
              player.classList.add("soundoff");
            } else {
              video.muted = false;
              player.classList.remove("soundoff");
            }
          });

          video.addEventListener("mouseenter", () => {
            player.classList.add("is-mouseenter");
          });
          video.addEventListener("mouseleave", () => {
            player.classList.remove("is-mouseenter");
          });

          // IS PLAYING
          video.addEventListener("playing", () => {
            player.classList.add("is-playing");
          });
          // IS PAUSE
          video.addEventListener("pause", () => {
            player.classList.remove("is-playing");
          });

          // TIMEUPDATE
          video.addEventListener("timeupdate", () => {
            gsap.set(bar, {
              width: `${(video.currentTime / video.duration) * 100}%`,
            });
          });
        }
      });
    }
  }
}

window.addEventListener("DOMContentLoaded", () => {
  new App();
});
